"use client"

import type React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import {
  FaFileExcel,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa"
import { FiFilter, FiX, FiRefreshCw, FiSearch } from "react-icons/fi"
import toast from "react-hot-toast"
import axiosInstance from "../../libs/axios"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { type SelectOption } from "../../utils/select-with-search"
import { useAuth } from "../../provider/auth.provider"
import { dateRangeError, dateRangeInputBounds } from "../../utils/date-range.utils"

interface PasswordRecord {
  id: number
  user_id: number
  user_name: string
  email: string
  password_changed_date_time: string
  changed_by?: string
  ip_address?: string
  device_type?: string
  reason?: string
}

interface PasswordReportFilters {
  user_id: string
  date_from: string
  date_to: string
  search: string
}

interface SortConfig {
  key: keyof PasswordRecord
  direction: "ASC" | "DESC"
}

const DEFAULT_FILTERS: PasswordReportFilters = {
  user_id: "",
  date_from: "",
  date_to: "",
  search: "",
}

export default function PasswordReport() {
  const { user } = useAuth()
  const [records, setRecords] = useState<PasswordRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState<PaginationType>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 0,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })
  const [filters, setFilters] = useState<PasswordReportFilters>(DEFAULT_FILTERS)
  const [pendingFilters, setPendingFilters] = useState<PasswordReportFilters>(DEFAULT_FILTERS)
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "password_changed_date_time",
    direction: "DESC",
  })
  const [users, setUsers] = useState<SelectOption[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true)
        const response = await axiosInstance.get("/users/list", {
          params: { limit: 1000 },
        })
        if (response.data?.data?.users) {
          const userOptions = response.data.data.users.map((u: any) => ({
            id: u.id,
            label: `${u.first_name} ${u.last_name}`,
            value: u.id,
          }))
          setUsers(userOptions)
        }
      } catch (error) {
        console.error("Error fetching users:", error)
      } finally {
        setUsersLoading(false)
      }
    }
    fetchUsers()
  }, [])

  const fetchPasswordRecords = useCallback(async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.user_id && { user_id: filters.user_id }),
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to }),
        ...(filters.search && { search: filters.search }),
        sort_by: sortConfig.key,
        sort_order: sortConfig.direction,
      }
      const response = await axiosInstance.get("/reports/password-records", { params })
      if (response.data?.data) {
        const recordsData = response.data.data.data || []
        const paginationData = response.data.data.pagination || {
          ...pagination,
          total_count: recordsData.length,
        }
        setRecords(recordsData)
        setPagination(paginationData)
      }
    } catch (error) {
      console.error("Error fetching password records:", error)
      toast.error("Failed to fetch password records")
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.limit, filters, sortConfig])

  useEffect(() => {
    fetchPasswordRecords()
  }, [fetchPasswordRecords])

  const handlePendingFilterChange = (key: keyof PasswordReportFilters, value: string) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }))
  }

  const handleApplyFilters = () => {
    const dateError = dateRangeError(pendingFilters.date_from, pendingFilters.date_to, "Date Range")
    if (dateError) {
      toast.error(dateError)
      return
    }
    setFilters(pendingFilters)
    setPagination((prev) => ({ ...prev, page: 1 }))
    setShowFilterModal(false)
  }

  const handleResetFilters = () => {
    setPendingFilters(DEFAULT_FILTERS)
    setFilters(DEFAULT_FILTERS)
    setSearchTerm("")
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleOpenFilterModal = () => {
    setPendingFilters(filters)
    setShowFilterModal(true)
  }

  const handleRemoveFilter = (key: keyof PasswordReportFilters) => {
    const updated = { ...filters, [key]: "" }
    setFilters(updated)
    setPendingFilters(updated)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleSort = (key: keyof PasswordRecord) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "ASC" ? "DESC" : "ASC",
    }))
  }

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }))
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    setFilters((prev) => ({ ...prev, search: value }))
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const getUserName = (userId: string) => {
    if (!users || !Array.isArray(users)) return userId
    const found = users.find((u) => String(u.value) === userId)
    return found?.label || userId
  }

  const getActiveFilterCount = () => {
    return Object.entries(filters).filter(([key, v]) => key !== "search" && v).length
  }

  const calculateDaysSinceChange = (dateString: string): number => {
    try {
      const parts = dateString.split(" ")
      const datePart = parts[2]
      const [day, month, year] = datePart.split("/")
      const changeDate = new Date(Number(year), Number(month) - 1, Number(day))
      const today = new Date()
      const diffTime = today.getTime() - changeDate.getTime()
      return Math.floor(diffTime / (1000 * 60 * 60 * 24))
    } catch {
      return 0
    }
  }

  const getPasswordAgeStatus = (dateString: string) => {
    const days = calculateDaysSinceChange(dateString)
    if (days > 90) {
      return { label: "Old", color: "bg-red-100 text-red-800" }
    } else if (days > 60) {
      return { label: "Aging", color: "bg-yellow-100 text-yellow-800" }
    } else {
      return { label: "Recent", color: "bg-green-100 text-green-800" }
    }
  }

  const exportData = useMemo(() => {
    return records.map((record) => ({
      "User Name": record.user_name,
      Email: record.email,
      "Last Password Changed": record.password_changed_date_time,
      "Changed By": record.changed_by || "N/A",
    }))
  }, [records])

  const SortIcon = ({ column }: { column: keyof PasswordRecord }) => {
    if (sortConfig.key !== column) return <FaSort className="text-gray-400 w-3 h-3" />
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="text-blue-800 w-3 h-3" />
    ) : (
      <FaSortDown className="text-blue-800 w-3 h-3" />
    )
  }

  const activeFilterCount = getActiveFilterCount()

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="w-full mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Password Report</h1>
          <p className="text-gray-600 mt-1">
            View last password change details for all users
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">

          {/* Card Header */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">Password Records</h2>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExcelModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaFileExcel className="w-4 h-4" />
                Export Excel
              </button>
            </div>
          </div>

          {/* Search & Filter Controls */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by user name or email..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Filter Button */}
              <div className="w-full sm:w-auto">
                <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
                <button
                  onClick={handleOpenFilterModal}
                  className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
                >
                  <FiFilter className="w-4 h-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Reset Button */}
              <div className="w-full sm:w-auto">
                <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
                <button
                  onClick={handleResetFilters}
                  className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors text-sm"
                >
                  <FiRefreshCw className="w-4 h-4" />
                  Reset
                </button>
              </div>

              <div className="ml-auto self-end pb-1 text-sm text-gray-600">
                Total Records:{" "}
                <span className="font-semibold text-gray-900">{pagination.total_count}</span>
              </div>
            </div>

            {/* Active Filter Chips */}
            {(filters.user_id || filters.date_from || filters.date_to) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                  {filters.user_id && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      User: {getUserName(filters.user_id)}
                      <button onClick={() => handleRemoveFilter("user_id")} className="hover:text-blue-900">
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.date_from && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      From: {filters.date_from}
                      <button onClick={() => handleRemoveFilter("date_from")} className="hover:text-blue-900">
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.date_to && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      To: {filters.date_to}
                      <button onClick={() => handleRemoveFilter("date_to")} className="hover:text-blue-900">
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  <button
                    onClick={handleResetFilters}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                <span className="text-sm text-gray-600">Loading records...</span>
              </div>
            ) : records.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <FaSearch className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No password records found</p>
                <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px]">
                      <button
                        onClick={() => handleSort("user_name")}
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                      >
                        User Name
                        <SortIcon column="user_name" />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                      <button
                        onClick={() => handleSort("password_changed_date_time")}
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                      >
                        Last Password Changed
                        <SortIcon column="password_changed_date_time" />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">
                      Password Age
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">
                      Changed By
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.map((record) => {
                    const ageStatus = getPasswordAgeStatus(record.password_changed_date_time)
                    const daysSince = calculateDaysSinceChange(record.password_changed_date_time)
                    return (
                      <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">
                            {record.user_name}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          {record.email}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          {record.password_changed_date_time}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${ageStatus.color}`}
                          >
                            {ageStatus.label} ({daysSince} days)
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          {record.changed_by || (
                            <span className="text-gray-400 italic">N/A</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!loading && records.length > 0 && (
            <div className="border-t border-gray-200 bg-gray-50">
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
                showInfo={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl max-w-md w-full flex flex-col overflow-hidden">

            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <FiFilter className="w-5 h-5" />
                  Filters
                </h2>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5 overflow-y-auto">
              {/* User Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
                <select
                  value={pendingFilters.user_id}
                  onChange={(e) => handlePendingFilterChange("user_id", e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="">All Users</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Date Range</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                    <input
                      type="date"
                      value={pendingFilters.date_from}
                      onChange={(e) => handlePendingFilterChange("date_from", e.target.value)}
                      {...dateRangeInputBounds(pendingFilters.date_from, pendingFilters.date_to).from}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                    <input
                      type="date"
                      value={pendingFilters.date_to}
                      onChange={(e) => handlePendingFilterChange("date_to", e.target.value)}
                      {...dateRangeInputBounds(pendingFilters.date_from, pendingFilters.date_to).to}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Search by user name or email..."
                  value={pendingFilters.search}
                  onChange={(e) => handlePendingFilterChange("search", e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => {
                  setPendingFilters(DEFAULT_FILTERS)
                  handleResetFilters()
                  setShowFilterModal(false)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
              >
                <FiRefreshCw className="w-4 h-4" />
                Reset Filters
              </button>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
                >
                  <FiSearch className="w-4 h-4" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Excel Export Modal */}
      {showExcelModal && (
        <ExcelDownload
          isOpen={showExcelModal}
          onClose={() => setShowExcelModal(false)}
          data={exportData}
          filename="password_report"
          title="Export Password Report"
          description="Download password change records in Excel format"
        />
      )}
    </div>
  )
}